import { cloneElement, FC, isValidElement, ReactElement, ReactNode } from 'react'

import { Box, Grid } from '@mui/material'

import { Analyst } from '../../../../../../components/Analyst/Analyst'
import { MaskedText } from '../../../../../../components/MaskedText/MaskedText'
import ShowMore from '../../../../../../components/ShowMore/ShowMore'
import { TranslationToggle } from '../../../../../../components/TranslationToggle/TranslationToggle'
import { getRandomIntBetween } from '../../../../../../helpers/random'
import { useContentTranslation, useStreamingTranslation } from '../../../../../../hooks/translationHooks'
import { Analyst as IAnalyst } from '../../../../../../types/Analyst'
import { useCurrentUserLanguage } from '../../../../../account/hooks/useCurrentUserLanguage'
import AskAnalystButton from '../../../../../ask-analyst/components/AskAnalystButton/AskAnalystButton'
import { Review } from '../../../../../game/types/GameReview'
import { NewsCardSectionDivider } from '../../NewsCardSectionDivider/NewsCardSectionDivider'
import type { NewsCardContentSectionProps } from '../NewsCardContentSections'
import './NewsCardContentAnalystContentSection.scss'

type NewsCardContentAnalystContentSectionProps = NewsCardContentSectionProps & {
  title: string
  analyst: IAnalyst
  rawContent?: string
  review?: Review
  children?: ReactNode
  showLimitedContent?: boolean
}

export const NewsCardContentAnalystContentSection: FC<NewsCardContentAnalystContentSectionProps> = ({
  newsEntry,
  title,
  analyst,
  rawContent,
  review,
  children,
  showLimitedContent,
}) => {
  const gameName = newsEntry.feedEntry.entryData.game
  const userLanguage = useCurrentUserLanguage()

  // For simple HTML content
  const {
    isTranslating: isHtmlTranslating,
    translatedContent,
    translationError: htmlTranslationError,
    handleTranslateClick,
    displayContent,
  } = useContentTranslation()

  // For complex Review objects
  const { isTranslating: isReviewTranslating, hasTranslation, error: reviewTranslationError, translatedReview, toggleTranslation } = useStreamingTranslation()

  // Determine which translation approach to use
  const isTranslatingHtml = rawContent && isHtmlTranslating
  const isTranslatingReview = review && isReviewTranslating
  const isTranslating = Boolean(isTranslatingHtml || isTranslatingReview)
  const hasAnyTranslation = Boolean(translatedContent || hasTranslation)
  const translationError = htmlTranslationError || reviewTranslationError
  const showTranslationToggle = (rawContent || review) && userLanguage !== 'en'

  // Clone children with appropriate props based on content type
  const renderedChildren = isValidElement(children)
    ? cloneElement(
        children as ReactElement<any>,
        rawContent
          ? {
              contentHtml: displayContent(rawContent),
              rawHtml: displayContent(rawContent),
            }
          : review
          ? {
              review: translatedReview || review,
              language: userLanguage,
            }
          : {}
      )
    : children

  const handleToggleTranslation = () => {
    if (rawContent) {
      handleTranslateClick(rawContent, userLanguage)
    } else if (review) {
      toggleTranslation(review, userLanguage)
    }
  }

  return (
    <Box className="NewsCardContentAnalystContentSection" mb={2}>
      <NewsCardSectionDivider>{title}</NewsCardSectionDivider>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <Analyst name={analyst?.name} picture={analyst?.picture} title={analyst?.title} />
        </Grid>
        <Grid item display="flex" alignItems="center" gap={2}>
          {showTranslationToggle && !showLimitedContent && (
            <TranslationToggle isTranslating={isTranslating} hasTranslation={hasAnyTranslation} error={translationError} onToggle={handleToggleTranslation} />
          )}
          {!showLimitedContent && <AskAnalystButton gameName={gameName} analyst={analyst}></AskAnalystButton>}
        </Grid>
      </Grid>
      <ShowMore collapsedSize={200}>
        <Box className="NewsCardContentAnalystContentSection__content">
          {showLimitedContent ? (
            <Box>
              <MaskedText length={getRandomIntBetween(70, 90)} />
              <MaskedText length={getRandomIntBetween(70, 90)} />
              <MaskedText length={getRandomIntBetween(70, 90)} />
              <MaskedText length={getRandomIntBetween(70, 90)} />
              <MaskedText length={getRandomIntBetween(70, 90)} />
              <MaskedText length={getRandomIntBetween(70, 90)} />
            </Box>
          ) : (
            renderedChildren
          )}
        </Box>
      </ShowMore>
    </Box>
  )
}
